/**
* Hook to manage widget UI state (open/close, typing, input, etc.)
*/

import { useCallback, useEffect, useRef, useState } from 'react'
import type { WidgetControls } from '../widget'

// Timeout in milliseconds - matches HOT Assistant
const AI_TYPING_TIMEOUT_MS = 30000

export interface UseWidgetStateReturn {
    isOpen: boolean
    setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void
    isSending: boolean
    setIsSending: (value: boolean) => void
    inputValue: string
    setInputValue: (value: string) => void
    isTyping: boolean
    setIsTyping: (value: boolean) => void
    messagesEndRef: React.RefObject<HTMLDivElement | null>
}

export function useWidgetState(
    onControlsReady: (controls: WidgetControls | null) => void,
    onStateChange?: () => void
): UseWidgetStateReturn {
    const [isOpen, setIsOpen] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [isTyping, setIsTypingInternal] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement | null>(null)
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Wrapped setIsTyping with timeout protection
    const setIsTyping = useCallback((value: boolean) => {
        // Clear any existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current)
            typingTimeoutRef.current = null
        }

        setIsTypingInternal(value)

        // If setting to true, start timeout protection
        if (value) {
            typingTimeoutRef.current = setTimeout(() => {
                setIsTypingInternal(false)
                typingTimeoutRef.current = null
            }, AI_TYPING_TIMEOUT_MS)
        }
    }, [])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        const controls: WidgetControls = {
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
            toggle: () => setIsOpen((prev) => !prev),
        }

        onControlsReady(controls)
        return () => {
            onControlsReady(null)
        }
    }, [onControlsReady])

    // Notify parent component about state changes to manage aria-hidden
    useEffect(() => {
        onStateChange?.()
    }, [isOpen, onStateChange])

    return {
        isOpen,
        setIsOpen,
        isSending,
        setIsSending,
        inputValue,
        setInputValue,
        isTyping,
        setIsTyping,
        messagesEndRef,
    }
}
